import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: {
        sitekey: string;
        callback?: (token: string) => void;
        "error-callback"?: () => void;
        "expired-callback"?: () => void;
        theme?: "light" | "dark" | "auto";
        appearance?: "always" | "execute" | "interaction-only";
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    __turnstileLoading?: Promise<void>;
  }
}

const SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;
  window.__turnstileLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SRC; s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script failed to load"));
    document.head.appendChild(s);
  });
  return window.__turnstileLoading;
}

interface P {
  sitekey: string;
  onToken: (token: string) => void;
  theme?: "light" | "dark" | "auto";
}

export function Turnstile({ sitekey, onToken, theme = "dark" }: P) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadScript().then(() => {
      if (cancelled || !boxRef.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(boxRef.current, {
        sitekey,
        theme,
        appearance: "always",
        callback: (t: string) => onToken(t),
        "error-callback": () => onToken(""),
        "expired-callback": () => onToken(""),
      });
    }).catch(() => {});
    return () => {
      cancelled = true;
      try { if (widgetId.current) window.turnstile?.remove(widgetId.current); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey]);

  return <div ref={boxRef} style={{ display: "flex", justifyContent: "center", minHeight: 70 }} />;
}
